import { coreGet, coreMutate } from "../../core-api/api-client";
import type {
  CaseLifecycleDetail,
  InquiryCenterResponse,
  InquirySavedView,
  ParallelDistributionRequest,
  SourcingDraftRequest,
} from "./types";

export function loadInquiryCenter(
  input: {
    view: InquirySavedView;
    search?: string;
    assigneeUserId?: string;
    risk?: string;
    overdue?: boolean;
    sort?: "UPDATED_DESC" | "UPDATED_ASC";
  },
  signal?: AbortSignal,
): Promise<InquiryCenterResponse> {
  const query = new URLSearchParams({ view: input.view });
  if (input.search) query.set("search", input.search);
  if (input.assigneeUserId) query.set("assigneeUserId", input.assigneeUserId);
  if (input.risk) query.set("risk", input.risk);
  if (input.overdue) query.set("overdue", "true");
  if (input.sort) query.set("sort", input.sort);
  return coreGet<InquiryCenterResponse>(
    `/business-flow/inquiries?${query.toString()}`,
    signal,
  );
}

export function loadCaseLifecycle(caseId: string, signal?: AbortSignal) {
  return coreGet<CaseLifecycleDetail>(
    `/business-flow/cases/${encodeURIComponent(caseId)}`,
    signal,
  );
}

export function distributeInquiry(
  inquiryId: string,
  body: ParallelDistributionRequest,
  idempotencyKey: string,
) {
  return coreMutate<{ caseId: string; caseNumber: string }>(
    `/business-flow/inquiries/${encodeURIComponent(inquiryId)}/distribute`,
    "POST",
    body,
    idempotencyKey,
  );
}

export function saveSourcingDraft(
  assignmentId: string,
  body: SourcingDraftRequest,
  idempotencyKey: string,
) {
  return coreMutate<{
    id: string;
    caseId: string;
    assignmentId: string;
    version: number;
    lockVersion: number;
    status: string;
  }>(
    `/business-flow/sourcing-assignments/${encodeURIComponent(assignmentId)}/draft`,
    "PUT",
    body,
    idempotencyKey,
  );
}

export function submitSourcingResult(
  assignmentId: string,
  resultId: string,
  expectedVersion: number,
  outcome: "SUBMITTED" | "NO_SUPPLY",
  idempotencyKey: string,
) {
  return coreMutate<{
    resultId: string;
    caseId: string;
    status: string;
    costSnapshotId: string;
    pricingHandoffId: string;
  }>(
    `/business-flow/sourcing-assignments/${encodeURIComponent(assignmentId)}/results/${encodeURIComponent(resultId)}/submit`,
    "POST",
    { expectedVersion, outcome },
    idempotencyKey,
  );
}

export interface CustomerQuoteDraftRequest {
  caseId: string;
  quoteNumber: string;
  currency: string;
  validUntil: string;
  paymentTerms: string;
  deliveryTerms: string;
  riskFlags: string[];
  customerClass?: string;
  pricing: {
    costCurrency: string;
    fxRate: string;
    fxSourceReference: string;
    fxEffectiveAt: string;
    quoteDiscountRate: string;
    freight: string;
    insurance: string;
    handling: string;
    taxRate: string;
    duty: string;
    surcharge: string;
    roundingScale: number;
  };
  lines: Array<{
    productReference?: string;
    description: string;
    quantity: string;
    unit: string;
    unitPrice: string;
    supplierOfferLineId: string;
    lineDiscountRate: string;
  }>;
}

export function createCaseQuote(
  body: CustomerQuoteDraftRequest,
  idempotencyKey: string,
) {
  return coreMutate<{
    id: string;
    customerQuoteId: string;
    versionNumber: number;
    status: string;
  }>("/customer-quotes", "POST", body, idempotencyKey);
}

export function submitQuoteApproval(
  quoteId: string,
  quoteVersionId: string,
  expectedVersion: number,
  idempotencyKey: string,
) {
  return coreMutate<Record<string, unknown>>(
    `/customer-quotes/${encodeURIComponent(quoteId)}/submit-review`,
    "POST",
    { quoteVersionId, expectedVersion, reason: "本地 UAT 客户报价提交审批" },
    idempotencyKey,
  );
}

export function decideCaseQuoteApproval(
  approvalId: string,
  expectedVersion: number,
  action: "APPROVE" | "REJECT" | "REQUEST_CHANGES",
  reason: string,
  idempotencyKey: string,
) {
  return coreMutate<Record<string, unknown>>(
    `/approvals/${encodeURIComponent(approvalId)}/decisions`,
    "POST",
    { expectedVersion, action, reason },
    idempotencyKey,
  );
}

export function generateQuoteDocument(
  versionId: string,
  idempotencyKey: string,
) {
  return coreMutate<{ id: string; status: string }>(
    `/quote-versions/${encodeURIComponent(versionId)}/documents`,
    "POST",
    { mode: "FINAL" },
    idempotencyKey,
  );
}

export function createQuoteDelivery(
  versionId: string,
  body: {
    documentId: string;
    mailboxId: string;
    conversationId: string;
    sender: { email: string };
    recipients: Array<{ email: string }>;
    subject: string;
    customerMessage: string;
  },
  idempotencyKey: string,
) {
  return coreMutate<{ id: string; status: string }>(
    `/quote-versions/${encodeURIComponent(versionId)}/deliveries`,
    "POST",
    body,
    idempotencyKey,
  );
}

export function approveQuoteDelivery(deliveryId: string) {
  return coreMutate<Record<string, unknown>>(
    `/quote-deliveries/${encodeURIComponent(deliveryId)}/approve`,
    "POST",
    {},
    `core-flow-delivery-approve-${crypto.randomUUID()}`,
  );
}

export function queueSyntheticQuoteDelivery(
  deliveryId: string,
  idempotencyKey: string,
) {
  return coreMutate<Record<string, unknown>>(
    `/quote-deliveries/${encodeURIComponent(deliveryId)}/queue-send`,
    "POST",
    {},
    idempotencyKey,
  );
}
